import type { Sales } from 'apiConnector/secondary-market/shared/clients';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { MapStateToPropsFactory } from 'react-redux';
import { createSelector } from 'reselect';
import type { SalesDesk } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import type { LimitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import type { LimitOrderThunks } from 'state/secondaryMarket/limit-order/limitOrder.thunk';
import type { LiveSelectors } from 'state/secondaryMarket/live/live.selector';
import type { LiveThunks } from 'state/secondaryMarket/live/live.thunk';
import type { SecondaryPriceType, SecondaryQuotationType } from 'state/secondaryMarket/productSearch';
import type { ClientType } from 'state/secondaryMarket/shared/form.models';
import type { OtcClient } from 'state/secondaryMarket/shared/referential/referential.model';
import type { ReferentialSelector } from 'state/secondaryMarket/shared/referential/referential.selector';
import { isClientSelected } from 'state/secondaryMarket/shared/utils';
import { getAvailableDesks, getAvailableSalesWithSelecteDesk } from 'state/secondaryMarket/utils';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { SecondaryWorkspaceType } from 'state/workspaces/model';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';
import type { LoadingStatus } from 'utils/status';

export type ProductSummaryOwnProps = { tabId: string };
export type ProductSummaryStateProps = {
  isinCode?: string;
  fileCode?: string;
  ric?: string;
  maturityDate?: string;
  productClients?: ReadonlyArray<ISalesCounterpart>;
  quotationType?: SecondaryQuotationType;
  priceType?: SecondaryPriceType;
  clientType: ClientType;
  isClientVisible: boolean;
  traderGroup?: string;
  isSalesVisible: boolean;
  portfolio?: string;
  isOtcProduct: boolean;
  traderMessage?: string;
  saleMessage?: string;
  clientLoadingStatus: LoadingStatus;
  client?: ISalesCounterpart;
  clientEliotCode?: string;
  productOtcClients?: ReadonlyArray<OtcClient>;
  salesId?: string;
  availableSales: Sales[];
  selectedSalesDesk?: string;
  availableDesks: readonly SalesDesk[];
  isFrozen: boolean;
};
export type ProductSummaryDispatchProps = {
  onClientTypeChanged: (value: ClientType) => void;
  onClientChanged: (clientBdrId: ISalesCounterpart) => void;
  onClientClear: () => void;
  onSalesChanged: (salesId: string | undefined) => void;
  onSalesDeskChanged: (salesId: string | undefined) => void;
};

export type ProductSummaryProps = ProductSummaryStateProps & ProductSummaryDispatchProps & ProductSummaryOwnProps;
export const secondaryMarketProductSummaryConnectFactory = (
  referentialSelectors: ReferentialSelector,
  secondaryMarketSelectors: LiveSelectors | LimitOrderSelectors,
  secondaryMarketThunks: LiveThunks | LimitOrderThunks,
  workspace: SecondaryWorkspaceType,
) => {
  const mapStateToProps: MapStateToPropsFactory<ProductSummaryStateProps, ProductSummaryOwnProps, AppState> = () => {
    const getAvailableSales = createSelector(
      [
        (appState: AppState, tabId: string) => referentialSelectors.getSales(appState, tabId),
        (appState: AppState, tabId: string) => secondaryMarketSelectors.getFormSalesDesk(appState, tabId),
      ],
      (allSales, selectedSalesDesk) => getAvailableSalesWithSelecteDesk(allSales, selectedSalesDesk),
    );

    const getDesks = createSelector(
      [(appState: AppState, tabId: string) => referentialSelectors.getSales(appState, tabId)],
      allSales => getAvailableDesks(allSales),
    );

    return (state, { tabId }) => {
      const isOtcProduct = referentialSelectors.isOtc(state, tabId) ?? false;
      const productClients = referentialSelectors.getAvailableClients(state, tabId);
      const productOtcClients = referentialSelectors.getAvailableOtcClients(state, tabId);
      const isinCode = referentialSelectors.getIsin(state, tabId);
      const ric = referentialSelectors.getRic(state, tabId);
      const maturityDate = referentialSelectors.getMaturityDate(state, tabId);
      const fileCode = referentialSelectors.getFileCode(state, tabId);
      const portfolio = referentialSelectors.getPortfolio(state, tabId)!;
      const quotationType = referentialSelectors.getQuotationType(state, tabId);
      const priceType = referentialSelectors.getPriceType(state, tabId)!;
      const traderGroup = referentialSelectors.getTraderGroup(state, tabId);
      const selectedClient = secondaryMarketSelectors.getFormClient(state, tabId);
      const clientType = secondaryMarketSelectors.getFormClientType(state, tabId);
      const isOtherClientType = clientType === 'Other';
      const clientEliotCode = selectedClient?.eliotCode;
      const salesId = secondaryMarketSelectors.getFormSalesId(state, tabId);
      const hasClient = isClientSelected(selectedClient);
      const selectedSalesDesk = secondaryMarketSelectors.getFormSalesDesk(state, tabId);
      const availableDesks = getDesks(state, tabId);
      const availableSales = getAvailableSales(state, tabId);
      const salesCounterpart = isOtherClientType ? selectedClient : undefined;
      const clientLoadingStatus = referentialSelectors.getClientLoadingStatus(state, tabId) ?? 'idle';
      const isFrozen = getTabIsFrozen(state, tabId) ?? false;
      const saleMessage = referentialSelectors.getSalesMessage(state, tabId);
      const traderMessage = referentialSelectors.getTraderMessage(state, tabId);
      return {
        isinCode,
        maturityDate,
        fileCode,
        ric,
        quotationType,
        priceType,
        isOtcProduct,
        client: salesCounterpart,
        productClients,
        productOtcClients,
        traderGroup,
        clientType,
        clientEliotCode,
        isClientVisible: isOtherClientType,
        isSalesVisible: hasClient,
        availableSales,
        availableDesks,
        selectedSalesDesk,
        salesId,
        portfolio,
        clientLoadingStatus,
        isFrozen,
        saleMessage,
        traderMessage,
      };
    };
  };

  const mapDispatchToProps = (
    dispatch: ThunkDispatch,
    { tabId }: ProductSummaryOwnProps,
  ): ProductSummaryDispatchProps => {
    return {
      onClientTypeChanged: value => {
        dispatch(secondaryMarketThunks.clientTypeSelectedThunk(tabId, workspace, value));
      },
      onClientChanged: client => {
        dispatch(secondaryMarketThunks.clientSelectedThunk(tabId, workspace, client));
      },
      onClientClear: () => {
        dispatch(secondaryMarketThunks.clientSelectedThunk(tabId, workspace, undefined));
      },
      onSalesChanged: salesId => {
        dispatch(secondaryMarketThunks.saleSelectedThunk(tabId, salesId));
      },
      onSalesDeskChanged: value => {
        dispatch(secondaryMarketThunks.saleDeskSelectedThunk(tabId, value));
      },
    };
  };

  return { mapStateToProps, mapDispatchToProps };
};
